import React, { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import "../../styles/lab/lab.css";
import { getSessions } from "../../services/infoCentre/session_formation_service";
import authService from "../../services/auth/authService";
import {
  STATUTS_NOTE,
  TYPES_EVALUATION,
  createNote,
  deleteNote,
  getFeuilleAppel,
  getNotes,
  publierNotes,
  updateNote,
} from "../../services/lab/pedagogieService";

const NOTE_VIDE = {
  inscription: "", type_evaluation: "devoir", titre_evaluation: "",
  note_obtenue: "", note_maximum: "20", date_evaluation: "", feedback: "",
};

const libelle = (liste, v) => liste.find((x) => x.value === v)?.label || v || "—";

export default function Notes() {
  // Saisie réservée à l'administration et aux formateurs (comme côté serveur,
  // EstPedagogique) : un étudiant consulte ses notes publiées, sans boutons
  // d'ajout, de modification ni de publication.
  const peutSaisir = authService.isPedagogique();

  const [sessions, setSessions]   = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [notes, setNotes]         = useState([]);
  const [inscriptions, setInscriptions] = useState([]);

  const [chargement, setChargement] = useState(false);
  const [flash, setFlash]         = useState(null);
  const [recherche, setRecherche] = useState("");
  const [filtreStatut, setFiltreStatut] = useState("");
  const [selection, setSelection] = useState(new Set());

  const [modale, setModale]   = useState(false);
  const [courante, setCourante] = useState(null);
  const [form, setForm]       = useState(NOTE_VIDE);
  const [erreurs, setErreurs] = useState({});
  const [envoi, setEnvoi]     = useState(false);

  const notifier = (msg, type = "succes") => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 4000);
  };

  useEffect(() => {
    getSessions()
      .then((s) => { setSessions(s); if (s.length) setSessionId(String(s[0].id)); })
      .catch(() => notifier("Impossible de charger les sessions.", "erreur"));
  }, []);

  const charger = useCallback(async () => {
    if (!sessionId) return;
    setChargement(true);
    setSelection(new Set());
    try {
      setNotes(await getNotes({ session: sessionId }));
    } catch {
      notifier("Impossible de charger les notes.", "erreur");
    } finally {
      setChargement(false);
    }
  }, [sessionId]);

  useEffect(() => { charger(); }, [charger]);

  // La feuille d'appel liste les inscrits de la session : c'est exactement la
  // liste des apprenants notables, et elle donne l'id d'inscription attendu.
  useEffect(() => {
    if (!sessionId || !peutSaisir) { setInscriptions([]); return; }
    getFeuilleAppel(sessionId, new Date().toISOString().slice(0, 10))
      .then(setInscriptions)
      .catch(() => setInscriptions([]));
  }, [sessionId, peutSaisir]);

  const filtrees = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return notes.filter((n) => {
      if (filtreStatut && n.statut !== filtreStatut) return false;
      if (!q) return true;
      return (n.apprenant_nom || "").toLowerCase().includes(q) ||
             (n.titre_evaluation || "").toLowerCase().includes(q);
    });
  }, [notes, recherche, filtreStatut]);

  const ouvrirCreation = () => {
    setForm({ ...NOTE_VIDE, date_evaluation: new Date().toISOString().slice(0, 10) });
    setCourante(null); setErreurs({}); setModale(true);
  };

  const ouvrirModification = (n) => {
    setForm({
      inscription: n.inscription,
      type_evaluation: n.type_evaluation,
      titre_evaluation: n.titre_evaluation || "",
      note_obtenue: n.note_obtenue ?? "",
      note_maximum: n.note_maximum ?? "20",
      date_evaluation: n.date_evaluation || "",
      feedback: n.feedback || "",
    });
    setCourante(n); setErreurs({}); setModale(true);
  };

  const enregistrer = async (e) => {
    e.preventDefault();
    setEnvoi(true); setErreurs({});
    const payload = {
      ...form,
      inscription: Number(form.inscription),
      note_obtenue: form.note_obtenue === "" ? null : Number(form.note_obtenue),
      note_maximum: Number(form.note_maximum),
      date_evaluation: form.date_evaluation || null,
    };
    try {
      if (courante) { await updateNote(courante.id, payload); notifier("Note modifiée."); }
      else { await createNote(payload); notifier("Note ajoutée."); }
      setModale(false);
      charger();
    } catch (err) {
      setErreurs(err.response?.data || { detail: "Erreur inattendue." });
    } finally { setEnvoi(false); }
  };

  const supprimer = async (n) => {
    if (!window.confirm(`Supprimer la note de ${n.apprenant_nom} ?`)) return;
    try { await deleteNote(n.id); notifier("Note supprimée."); charger(); }
    catch { notifier("Suppression impossible.", "erreur"); }
  };

  const basculer = (id) => {
    setSelection((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const publier = async () => {
    if (!selection.size) return;
    try {
      const r = await publierNotes([...selection]);
      notifier(r.message);
      charger();
    } catch { notifier("Publication impossible.", "erreur"); }
  };

  const enAttente = filtrees.filter((n) => n.statut === "en_attente");
  const sessionCourante = sessions.find((s) => String(s.id) === String(sessionId));

  return (
    <Layout>
      <div className="lab-page">
        <header className="lab-header">
          <div>
            <h1><i className="fa-solid fa-star-half-stroke"></i> {peutSaisir ? "Notes" : "Mes notes"}</h1>
            <p className="lab-sous-titre">
              {peutSaisir
                ? "Évaluations — une note n'est visible par l'apprenant qu'une fois publiée"
                : "Vos évaluations, dès que votre formateur les publie"}
            </p>
          </div>
          {peutSaisir && <div className="lab-actions-tete">
            {selection.size > 0 && (
              <button className="btn-secondaire" onClick={publier}>
                <i className="fa-solid fa-paper-plane"></i> Publier ({selection.size})
              </button>
            )}
            <button className="btn-primaire" onClick={ouvrirCreation}>
              <i className="fa-solid fa-plus"></i> Nouvelle note
            </button>
          </div>}
        </header>

        {flash && <div className={`lab-flash ${flash.type}`}>{flash.msg}</div>}

        <div className="lab-barre">
          <label className="lab-champ">
            Session
            <select value={sessionId} onChange={(e) => setSessionId(e.target.value)}>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>{s.intitule_session}</option>
              ))}
            </select>
          </label>
          {peutSaisir && <label className="lab-champ">
            Statut
            <select value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
              <option value="">Tous</option>
              {STATUTS_NOTE.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </label>}
          <div className="lab-recherche">
            <i className="fa-solid fa-magnifying-glass"></i>
            <input placeholder={peutSaisir ? "Rechercher un apprenant, une évaluation…" : "Rechercher une évaluation…"} value={recherche}
                   onChange={(e) => setRecherche(e.target.value)} />
          </div>
        </div>

        {sessionCourante && (
          <div className="lab-contexte">
            <span><i className="fa-solid fa-book-open"></i> {sessionCourante.formation_nom}</span>
            <span><i className="fa-solid fa-star-half-stroke"></i> {notes.length} note(s)</span>
            {peutSaisir && enAttente.length > 0 && (
              <span><i className="fa-solid fa-clock"></i> {enAttente.length} en attente de publication</span>
            )}
          </div>
        )}

        {chargement && <div className="lab-etat"><i className="fa-solid fa-spinner fa-spin"></i> Chargement…</div>}

        {!chargement && (
          <div className="notes-table">
            <div className="notes-entete">
              <span className="col-check">
                {peutSaisir && !!enAttente.length && (
                  <input type="checkbox"
                         checked={selection.size === enAttente.length && enAttente.length > 0}
                         onChange={(e) => setSelection(e.target.checked
                           ? new Set(enAttente.map((n) => n.id)) : new Set())} />
                )}
              </span>
              <span>Apprenant</span>
              <span>Évaluation</span>
              <span className="col-note">Note</span>
              <span className="col-pct">%</span>
              <span>Date</span>
              <span>Statut</span>
              <span className="col-act"></span>
            </div>

            {filtrees.map((n) => (
              <div key={n.id} className={`notes-ligne statut-${n.statut}`}>
                <span className="col-check">
                  {peutSaisir && n.statut === "en_attente" && (
                    <input type="checkbox" checked={selection.has(n.id)}
                           onChange={() => basculer(n.id)} />
                  )}
                </span>
                <span className="notes-apprenant">{n.apprenant_nom}</span>
                <span>
                  <strong>{libelle(TYPES_EVALUATION, n.type_evaluation)}</strong>
                  {n.titre_evaluation && <em> — {n.titre_evaluation}</em>}
                </span>
                <span className="col-note">
                  <strong>{n.note_obtenue ?? "—"}</strong>
                  <small>/{n.note_maximum}</small>
                </span>
                <span className="col-pct">
                  {n.pourcentage !== null && n.pourcentage !== undefined ? (
                    <span className={`notes-pct ${n.pourcentage >= 50 ? "ok" : "bas"}`}>
                      {n.pourcentage} %
                    </span>
                  ) : "—"}
                </span>
                <span className="notes-date">
                  {n.date_evaluation ? new Date(n.date_evaluation).toLocaleDateString("fr-FR") : "—"}
                </span>
                <span>
                  <span className={`salle-badge ${n.statut === "publiee" ? "disponible" : n.statut === "archivee" ? "terminee" : "maintenance"}`}>
                    {n.statut_display}
                  </span>
                </span>
                <span className="col-act">
                  {peutSaisir && <>
                    <button onClick={() => ouvrirModification(n)} title="Modifier">
                      <i className="fa-solid fa-pen"></i>
                    </button>
                    <button className="danger" onClick={() => supprimer(n)} title="Supprimer">
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </>}
                </span>
              </div>
            ))}

            {!filtrees.length && (
              <div className="lab-vide">
                <i className="fa-regular fa-star"></i>
                <p>Aucune note pour cette session.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {peutSaisir && modale && (
        <div className="lab-modale-fond" onClick={() => setModale(false)}>
          <div className="lab-modale" onClick={(e) => e.stopPropagation()}>
            <header>
              <h2>{courante ? "Modifier la note" : "Nouvelle note"}</h2>
              <button onClick={() => setModale(false)}><i className="fa-solid fa-xmark"></i></button>
            </header>
            <form onSubmit={enregistrer}>
              <div className="champs-colonne">
                <label>Apprenant *
                  <select value={form.inscription} required disabled={!!courante}
                          onChange={(e) => setForm({ ...form, inscription: e.target.value })}>
                    <option value="">Choisir…</option>
                    {inscriptions.map((i) => (
                      <option key={i.inscription_id} value={i.inscription_id}>
                        {i.apprenant_nom}{i.matricule ? ` — ${i.matricule}` : ""}
                      </option>
                    ))}
                  </select>
                  {erreurs.inscription && <small className="err">{erreurs.inscription}</small>}
                </label>

                <div className="champs-duo">
                  <label>Type *
                    <select value={form.type_evaluation}
                            onChange={(e) => setForm({ ...form, type_evaluation: e.target.value })}>
                      {TYPES_EVALUATION.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </label>
                  <label>Date
                    <input type="date" value={form.date_evaluation}
                           onChange={(e) => setForm({ ...form, date_evaluation: e.target.value })} />
                  </label>
                </div>

                <label>Intitulé
                  <input value={form.titre_evaluation} placeholder="Ex : Projet final"
                         onChange={(e) => setForm({ ...form, titre_evaluation: e.target.value })} />
                </label>

                <div className="champs-duo">
                  <label>Note obtenue
                    <input type="number" step="0.25" min="0" value={form.note_obtenue}
                           onChange={(e) => setForm({ ...form, note_obtenue: e.target.value })} />
                    {erreurs.note_obtenue && <small className="err">{erreurs.note_obtenue}</small>}
                  </label>
                  <label>Barème *
                    <input type="number" step="0.5" min="1" value={form.note_maximum} required
                           onChange={(e) => setForm({ ...form, note_maximum: e.target.value })} />
                    {erreurs.note_maximum && <small className="err">{erreurs.note_maximum}</small>}
                  </label>
                </div>

                <label>Commentaire
                  <textarea rows="3" value={form.feedback}
                            onChange={(e) => setForm({ ...form, feedback: e.target.value })} />
                </label>
              </div>

              {erreurs.detail && <div className="lab-flash erreur">{erreurs.detail}</div>}

              <footer>
                <button type="button" className="btn-lien" onClick={() => setModale(false)}>Annuler</button>
                <button type="submit" className="btn-primaire" disabled={envoi}>
                  {envoi ? "Enregistrement…" : "Enregistrer"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
